import { FC } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box,
  formattedCurrency,
  RTRWithdrawTransactionResponse,
  Card,
  withStyles,
  capitalizeFirstLetter,
  TransactionType,
} from 'vopay';
import { TransactionCard, Title, DetailsRows, TransactionStatus } from 'UI';
import BankDetailsCard from './Card/BankDetailsCard';
import RecipientDetailsCard from './Card/RecipientDetailsCard';
import ApprovalHistoryDetailsCard from './Card/ApprovalHistoryDetailsCard';
import styles from '../Transactions.module.scss';
import { useClientData } from '@/providers/ClientData.provider';

const cn = withStyles(styles);

type TransactionInfoProps = {
  transaction: RTRWithdrawTransactionResponse;
  Notes: string;
  isParent?: boolean;
  jwt: string;
};

type DataProps = {
  hasErrorInfo: boolean;
  failureReason: string;
  accountId: string;
};

const VopayInstantWithdrawalInfo: FC<TransactionInfoProps> = ({ transaction, isParent = false, jwt }) => {
  const { state } = useLocation<DataProps>();
  const { t } = useTranslation('translations', {
    keyPrefix: 'Transaction.TransactionHistory.Details.VopayInstantWithdrawalInfo',
  });
  const { t: tGlobal } = useTranslation('translations');
  const {
    response: { JWT: mainJwt },
  } = useClientData();
  const isMainAccount = !jwt || jwt === mainJwt;
  const transactionDetails = [
    {
      label: t('TransactionDetails.transactionStatus', { defaultValue: 'Transaction Status' }),
      value: (
        <Box jc="fe">
          <TransactionStatus
            status={transaction.TransactionStatus}
            label={capitalizeFirstLetter(
              tGlobal(`Transaction.TransactionHistory.statuses.${transaction.TransactionStatus}`, {
                defaultValue: transaction.TransactionStatus,
              }),
            )}
          />
        </Box>
      ),
      visible: isParent,
    },
    {
      label: t('TransactionDetails.type', { defaultValue: 'Transaction Type' }),
      value: tGlobal('Transaction.TransactionHistory.transactionTypes.VoPayInstant Withdraw', {
        defaultValue: 'VoPayInstant Withdraw',
      }),
    },
    {
      label: t('TransactionDetails.transactionTypeCode', { defaultValue: 'Transaction Type Code' }),
      value: transaction.TransactionTypeCode || '-',
    },
    {
      label: t('TransactionDetails.transactionDate', { defaultValue: 'Transaction Date' }),
      value: transaction.TransactionDateTime,
    },
    {
      label: t('TransactionDetails.processingDate', { defaultValue: 'Processing Date' }),
      value: transaction.ProcessingDate || '-',
    },
    {
      label: t('TransactionDetails.lastModified', { defaultValue: 'Last Modified' }),
      value: transaction.LastModified,
    },
    {
      label: t('TransactionDetails.amount', { defaultValue: 'Amount' }),
      value: transaction.Currency + ' ' + formattedCurrency(transaction.Amount),
    },
    {
      label: t('TransactionDetails.holdAmount', { defaultValue: 'Hold Amount' }),
      value: transaction.HoldAmount ? `${transaction.Currency} ${formattedCurrency(transaction.HoldAmount)}` : '-',
    },
    {
      label: t('TransactionDetails.clientReferenceNumber', { defaultValue: 'Client Reference Number' }),
      value: transaction.ClientReferenceNumber || '-',
    },
    {
      label: t('TransactionDetails.glCode', { defaultValue: 'GL Code' }),
      value: transaction.GLCode || '-',
    },
    {
      label: t('ErrorInfo.failureReason', { defaultValue: 'Failure Reason' }),
      value: state?.failureReason || '-',
      visible: isParent && state.failureReason !== '',
    },
  ];

  const errorDetails = [
    {
      label: t('ErrorInfo.failureDateTime', { defaultValue: 'Failure Date Time' }),
      value: transaction.LastModified,
    },
    {
      label: t('ErrorInfo.amount', { defaultValue: 'Amount' }),
      value: `${transaction.Currency} ${formattedCurrency(transaction.Amount)}`,
    },
    {
      label: t('ErrorInfo.failureReason', { defaultValue: 'Failure Reason' }),
      value: state?.failureReason || '-',
      visible: !isParent && state?.failureReason !== '',
    },
  ];

  return (
    <Box flx="col" gapSize="24px">
      {!isParent && (
        <TransactionCard
          receiverName={transaction.RecipientName}
          amount={transaction.Amount}
          currency={transaction.Currency}
          transactionId={transaction.TransactionID}
          type={'VoPayInstant Withdraw'}
          failureReason={state?.failureReason}
          contactId={state?.accountId}
          jwt={jwt}
          isMainAccount={isMainAccount}
        />
      )}

      <Card className={cn('card')}>
        <Title level="4" className={cn('title')}>
          {t('transactionDetailsHeader', { defaultValue: 'Transaction Details' })}
        </Title>
        <DetailsRows className={cn('rows')} details={transactionDetails} />
      </Card>

      <ApprovalHistoryDetailsCard
        transactionID={transaction.TransactionID}
        status={transaction.TransactionStatus}
        type={TransactionType.VOPAYINSTANT_WITHDRAWAL}
        jwt={jwt}
      />

      <RecipientDetailsCard
        isMainAccount={isMainAccount}
        receiverName={transaction.RecipientName}
        accountID={state?.accountId}
      />

      <BankDetailsCard
        details={{
          AccountNumber: transaction.AccountNumber,
          FinancialInstitutionNumber: transaction.FinancialInstitutionNumber,
          BranchTransitNumber: transaction.BranchTransitNumber,
        }}
      />

      {state?.hasErrorInfo && (
        <Card className={cn('card')}>
          <Title level="4" className={cn('title')}>
            {t('errorInfoHeader', { defaultValue: 'Error Info' })}
          </Title>
          <DetailsRows className={cn('rows')} details={errorDetails} />
        </Card>
      )}
    </Box>
  );
};

export default VopayInstantWithdrawalInfo;
